import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Box,
  Typography,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FolderOpen as TemplateIcon,
} from '@mui/icons-material';
import type { VorlageCardProps } from './types';

const VorlageCard: React.FC<VorlageCardProps> = ({
  vorlage,
  onExecute,
  onEdit,
  onDelete,
  isDeleting,
  formatDate,
}) => {
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="h6" component="div">
            {vorlage.name}
          </Typography>
          <Chip
            label={vorlage.istAktiv ? 'Aktiv' : 'Inaktiv'}
            color={vorlage.istAktiv ? 'success' : 'default'}
            size="small"
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Chip
            icon={<TemplateIcon />}
            label={vorlage.kategorie}
            variant="outlined"
            size="small"
            sx={{ mr: 1 }}
          />
        </Box>

        {vorlage.beschreibung && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {vorlage.beschreibung}
          </Typography>
        )}

        <Divider sx={{ my: 1 }} />

        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Schritte:</strong> {vorlage.schritte.length}
        </Typography>

        <List dense disablePadding>
          {vorlage.schritte.slice(0, 3).map((schritt) => (
            <ListItem key={schritt.schrittNummer} disablePadding sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Chip label={schritt.schrittNummer} size="small" variant="outlined" />
              </ListItemIcon>
              <ListItemText
                primary={schritt.titel}
                primaryTypographyProps={{ variant: 'body2', noWrap: true }}
              />
            </ListItem>
          ))}
          {vorlage.schritte.length > 3 && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
              ... und {vorlage.schritte.length - 3} weitere Schritte
            </Typography>
          )}
        </List>

        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Erstellt: {formatDate(vorlage.erstelltAm)}
          {vorlage.erstelltVonName && ` von ${vorlage.erstelltVonName}`}
        </Typography>
      </CardContent>
      <CardActions sx={{ justifyContent: 'space-between' }}>
        <Button
          size="small"
          variant="contained"
          color="primary"
          startIcon={<PlayArrowIcon />}
          onClick={() => onExecute(vorlage)}
          disabled={!vorlage.istAktiv}
        >
          Workflow starten
        </Button>
        <Box>
          <Tooltip title="Vorlage bearbeiten">
            <IconButton
              size="small"
              color="primary"
              onClick={() => onEdit(vorlage)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Vorlage löschen">
            <IconButton
              size="small"
              color="error"
              onClick={() => onDelete(vorlage.vorlageID)}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <CircularProgress size={20} />
              ) : (
                <DeleteIcon />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      </CardActions>
    </Card>
  );
};

export default VorlageCard;
